// frontend/src/widgets/WorkflowPublishControlsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 16:35 BST
//
// Description:
//   Workflow publish controls widget.
//
//   Responsibilities:
//     - Summarise selected template/version publish state
//     - Gate publish intent behind validation readiness
//     - Allow operator to provide final publish change summary
//     - Trigger final publish action
//
//   Important:
//     - This is UI-side gating only
//     - Backend remains the authority for publish rules
//     - Only draft versions can be published
//
// Mobile Responsiveness (2026-09-10):
//     - Preserves all publish-state evaluation, validation gating and publish callback semantics.
//     - Uses the existing single-column content flow on desktop and mobile.
//     - Publish context/status text wraps safely on narrow screens.
//     - Final Change Summary remains full-width and becomes touch-safe on mobile.
//     - Publish Draft Version remains full-width and uses a minimum 44px mobile touch target.
//     - Dynamic mobile sizing uses the proven Vision full-form pattern:
//         restore previous host sizing;
//         clear temporary root/content sizing;
//         measure the complete natural rendered content;
//         wait a second animation frame;
//         resize the first width-matching fixed-height mobile host.
//     - ResizeObserver watches the complete content so status/validation/textarea height changes
//       cannot leave the widget clipped.
//     - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//     - UI-side publish gating remains advisory; backend remains authoritative.
//     - Only draft versions can be published.
//     - onPublishVersion remains the sole mutation path and the change_summary payload is unchanged.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const WorkflowPublishControlsWidget = ({
  selectedTemplate = null,
  selectedVersion = null,
  validation = {
    errors: [],
    warnings: [],
    info: [],
    isReadyToPublish: false,
  },
  loading = {
    publishing: false,
  },
  onPublishVersion,
}) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);

  const [changeSummary, setChangeSummary] = useState("");

  useEffect(() => {
    setChangeSummary(selectedVersion?.change_summary || "");
  }, [selectedVersion]);

  const publishable = useMemo(() => {
    return (
      !!selectedTemplate?.id &&
      !!selectedVersion?.id &&
      selectedVersion.status === "draft"
    );
  }, [selectedTemplate, selectedVersion]);

  const canPublish =
    publishable &&
    !!validation?.isReadyToPublish &&
    !loading.publishing;

  const actionBtn = {
    background: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "10px 14px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
  };

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    padding: "8px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    boxSizing: "border-box",
    fontSize: 14,
  };

  const muted = {
    fontSize: 12,
    color: "#6b7280",
  };

  const handlePublish = () => {
    if (!publishable) {
      alert("Only draft versions can be published.");
      return;
    }

    if (!validation?.isReadyToPublish) {
      alert("Resolve the items marked as needing attention before publishing.");
      return;
    }

    onPublishVersion?.({
      change_summary:
        String(changeSummary || "").trim() || null,
    });
  };

  useLayoutEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.matchMedia
    ) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(
      "(max-width: 767px)"
    );

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height =
        hostRecord.height;
      hostRecord.node.style.minHeight =
        hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect =
        root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (
        node &&
        node !== document.body &&
        depth < 6
      ) {
        const rect =
          node.getBoundingClientRect();

        const inlineHeight = String(
          node.style?.height || ""
        ).trim();

        const widthMatches =
          Math.abs(
            rect.width - rootRect.width
          ) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (
          widthMatches &&
          hasFixedInlineHeight
        ) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length
        ? candidates[0]
        : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame =
        requestAnimationFrame(() => {
          const naturalContentHeight =
            Math.ceil(
              Math.max(
                content.getBoundingClientRect()
                  .height || 0,
                content.scrollHeight || 0
              )
            );

          root.style.height = "auto";
          root.style.minHeight =
            Number.isFinite(
              naturalContentHeight
            ) &&
            naturalContentHeight > 0
              ? `${naturalContentHeight}px`
              : "0px";

          content.style.height = "auto";
          content.style.minHeight = "0px";

          animationFrame =
            requestAnimationFrame(() => {
              const host =
                findFixedMobileHost();

              if (!host) {
                return;
              }

              hostRecord = {
                node: host,
                height: host.style.height,
                minHeight:
                  host.style.minHeight,
              };

              const hostRect =
                host.getBoundingClientRect();

              const rootRect =
                root.getBoundingClientRect();

              const wrapperChromeHeight =
                Math.max(
                  0,
                  rootRect.top -
                    hostRect.top
                );

              const requiredHostHeight =
                Math.ceil(
                  Math.max(
                    host.scrollHeight,
                    wrapperChromeHeight +
                      rootRect.height +
                      8
                  )
                );

              if (
                !Number.isFinite(
                  requiredHostHeight
                ) ||
                requiredHostHeight <= 0
              ) {
                return;
              }

              host.style.height =
                `${requiredHostHeight}px`;

              host.style.minHeight =
                `${requiredHostHeight}px`;
            });
        });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() =>
            applyMobileSizing()
          )
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () =>
      applyMobileSizing();

    mediaQuery.addEventListener?.(
      "change",
      handleMediaChange
    );

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.(
        "change",
        handleMediaChange
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    canPublish,
    changeSummary,
    loading.publishing,
    publishable,
    selectedTemplate?.id,
    selectedTemplate?.name,
    selectedTemplate?.template_key,
    selectedVersion?.id,
    selectedVersion?.status,
    validation?.errors?.length,
    validation?.warnings?.length,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-workflow-publish-content textarea {
            min-height: 140px !important;
          }

          .vx-workflow-publish-button {
            min-height: 44px;
          }
        }
      `}</style>

      <div
        ref={contentRef}
        className="vx-workflow-publish-content flex h-auto min-h-0 flex-col gap-3.5 md:h-full"
      >
        <div>
          <div
            style={{
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            Publish State
          </div>

          <div style={muted}>
            Publish the selected draft when its business tasks are ready
          </div>
        </div>

        <div
          className="min-w-0 break-words"
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 12,
            background: "#fafafa",
          }}
        >
          <div
            className="break-words"
            style={{
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            {selectedTemplate?.name ||
              "No template selected"}
          </div>

          <div
            className="break-all"
            style={muted}
          >
            Template Key:{" "}
            {selectedTemplate?.template_key ||
              "—"}
          </div>

          <div
            className="break-words"
            style={muted}
          >
            Version:{" "}
            {selectedVersion
              ? `v${selectedVersion.version_no}${
                  selectedVersion.version_label
                    ? ` — ${selectedVersion.version_label}`
                    : ""
                }`
              : "—"}
          </div>

          <div style={muted}>
            Version Status:{" "}
            {selectedVersion?.status || "—"}
          </div>

          <div style={muted}>
            Blocking Errors:{" "}
            {validation?.errors?.length || 0}
          </div>

          <div style={muted}>
            Warnings:{" "}
            {validation?.warnings?.length || 0}
          </div>
        </div>

        <div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#374151",
              marginBottom: 4,
            }}
          >
            Final Change Summary
          </div>

          <textarea
            value={changeSummary}
            onChange={(e) =>
              setChangeSummary(e.target.value)
            }
            style={{
              ...inputStyle,
              minHeight: 120,
              resize: "vertical",
            }}
            placeholder="Summarise what changed in this version before publish..."
          />
        </div>

        <div
          className="break-words"
          style={{
            borderRadius: 10,
            padding: 12,
            background: canPublish
              ? "#dcfce7"
              : "#fef2f2",
            color: canPublish
              ? "#166534"
              : "#991b1b",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {canPublish
            ? "This draft is ready to publish."
            : !publishable
              ? "Select a draft version to publish."
              : "This draft is not publishable until validation errors are cleared."}
        </div>

        <div className="mt-0 md:mt-auto">
          <button
            type="button"
            className="vx-workflow-publish-button"
            onClick={handlePublish}
            disabled={!canPublish}
            style={{
              ...actionBtn,
              width: "100%",
              opacity: !canPublish
                ? 0.7
                : 1,
              cursor: !canPublish
                ? "not-allowed"
                : loading.publishing
                  ? "progress"
                  : "pointer",
            }}
          >
            {loading.publishing
              ? "Publishing..."
              : "Publish Draft Version"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowPublishControlsWidget;
